/**
 * Studio Booking · Widget config editor — extracted Oct 2026.
 * Owns the entire "Booking page" tab: branding, theme, hero text,
 * entry tiles, deep-link builder, social preview, mail-profile picker.
 *
 * All children (Section, Field, ToggleRow, BookingSlugField,
 * QuickShareUrl, SocialPreviewField, DeepLinkBuilder, EntryTilesEditor,
 * StudioMailProfilePicker) are used exclusively inside this file so
 * they stay here as internal helpers.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Copy, Eye, Save, Bell, CalendarDays, MessageCircle, RefreshCw, Send, X as XIcon } from "lucide-react";
import { ImageUpload } from "./MediaUpload";

export function WidgetConfigEditor({ ws, canManage }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/widget-config`);
      setCfg(data.widget_config || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);
  const upd = (patch) => setCfg({ ...(cfg || {}), ...patch });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${ws}/studio/widget-config`, cfg || {});
      toast.success("Booking page updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  if (!cfg) return <div className="text-sm text-muted-foreground italic">Loading…</div>;
  const dis = !canManage;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="sb-widget-config">
      {!canManage && (
        <div className="border border-amber-500 bg-amber-50 p-2 text-[11px] text-amber-800">
          You have read-only access to booking page settings. Ask an admin for <code>studio.settings.manage</code> to edit.
        </div>
      )}
      <Section title="Hero">
        <Field label="Cover / hero image (desktop · 16:9)">
          <ImageUpload ws={ws} purpose="hero" value={cfg.hero_image_url}
                        onChange={(url) => upd({ hero_image_url: url })}
                        testid="sb-wc-hero-image" disabled={dis}
                        zoomMode aspect={16 / 9} />
        </Field>
        <Field label="Mobile hero image (portrait · 4:5) — optional">
          <ImageUpload ws={ws} purpose="hero" value={cfg.hero_image_url_mobile}
                        onChange={(url) => upd({ hero_image_url_mobile: url })}
                        testid="sb-wc-hero-image-mobile" disabled={dis}
                        zoomMode aspect={4 / 5} />
          <div className="text-[11px] text-muted-foreground mt-1">
            Falls back to the desktop hero when empty. Portrait crop looks better on phones.
          </div>
        </Field>
        <Field label="Page title (overrides workspace name — appears in the eyebrow above tagline)">
          <Input value={cfg.title || ""} onChange={(e) => upd({ title: e.target.value })}
                 disabled={dis} className="rounded-none h-9" data-testid="sb-wc-title"
                 placeholder="Blank = show workspace name" />
        </Field>
        <Field label="Hide workspace-name eyebrow entirely">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.hide_workspace_name}
                    onChange={(e) => upd({ hide_workspace_name: e.target.checked })}
                    disabled={dis} data-testid="sb-wc-hide-workspace-name" />
            <span>Don&apos;t show workspace name (title also hidden when checked)</span>
          </label>
        </Field>
        <Field label="Tagline">
          <Input value={cfg.tagline || ""} onChange={(e) => upd({ tagline: e.target.value })}
                 disabled={dis} className="rounded-none h-9" data-testid="sb-wc-tagline"
                 placeholder="Mumbai's most-booked photo & podcast studio" />
        </Field>
        <Field label="Subtitle (bold sentence directly under the tagline)">
          <Input value={cfg.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
                 disabled={dis} className="rounded-none h-9" data-testid="sb-wc-subtitle"
                 placeholder="Book 24×7, pay securely, get instant confirmation" />
        </Field>
        <Field label="Welcome text (short paragraph, HTML allowed)">
          <Textarea value={cfg.welcome_text || ""} onChange={(e) => upd({ welcome_text: e.target.value })}
                    disabled={dis} rows={3} className="rounded-none text-xs" data-testid="sb-wc-welcome" />
        </Field>
      </Section>

      {/* Feb 2026 · R268mmm · Custom booking-URL slug — replaces the
          long UUID with a memorable brand slug. */}
      <Section title="Custom booking URL">
        <BookingSlugField cfg={cfg} upd={upd} dis={dis} ws={ws} />
      </Section>

      {/* Feb 2026 · R268sss · Layout mode + WhatsApp preview link. */}
      <Section title="Layout & sharing">
        <Field label="Booking flow layout">
          {/* R268xxx · Feb 2026 · Subtle Steps toggle. Rendered as an
              iOS-style pill instead of two verbose radios — feels
              like a preference, not a form. On = Steps, Off = Compact. */}
          <div className="flex items-center gap-3 py-1" data-testid="sb-wc-layout-mode">
            <button type="button" role="switch"
                     aria-checked={(cfg.layout_mode || "steps") === "steps"}
                     disabled={dis}
                     onClick={() => upd({ layout_mode: (cfg.layout_mode === "compact") ? "steps" : "compact" })}
                     data-testid="sb-wc-layout-mode-toggle"
                     className={`relative inline-flex h-5 w-9 items-center transition-colors border ${
                       (cfg.layout_mode || "steps") === "steps"
                         ? "bg-primary border-primary"
                         : "bg-muted border-border"
                     } ${dis ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
              <span className={`inline-block h-3 w-3 bg-background transition-transform ${
                (cfg.layout_mode || "steps") === "steps" ? "translate-x-5" : "translate-x-1"
              }`} />
            </button>
            <div className="flex-1">
              <div className="text-xs font-semibold">
                Steps: {(cfg.layout_mode || "steps") === "steps" ? "ON — hero + progress bar" : "OFF — compact one-page"}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Customers can override per URL with{" "}
                <code className="font-mono">?mode=steps</code> /{" "}
                <code className="font-mono">?mode=compact</code>.
              </div>
            </div>
          </div>
        </Field>
        {/* R268xxx · Quick share URL — one-tap copy right below the
            layout selector for the common "just send me the link" case. */}
        <QuickShareUrl cfg={cfg} ws={ws} />
        <SocialPreviewField cfg={cfg} upd={upd} dis={dis} ws={ws} />
        <DeepLinkBuilder cfg={cfg} ws={ws} />
      </Section>

      <Section title="Branding">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Primary color">
            <Input type="color" value={cfg.primary_color || "#EA5934"} onChange={(e) => upd({ primary_color: e.target.value })}
                    disabled={dis} className="rounded-none h-9 w-24 p-1" data-testid="sb-wc-primary" />
          </Field>
          <Field label="Accent color">
            <Input type="color" value={cfg.accent_color || "#0B2545"} onChange={(e) => upd({ accent_color: e.target.value })}
                    disabled={dis} className="rounded-none h-9 w-24 p-1" data-testid="sb-wc-accent" />
          </Field>
          <Field label="Heading color (big titles)">
            <Input type="color" value={cfg.heading_color || "#241A11"} onChange={(e) => upd({ heading_color: e.target.value })}
                    disabled={dis} className="rounded-none h-9 w-24 p-1" data-testid="sb-wc-heading-color" />
          </Field>
          <Field label="Text color (body copy)">
            <Input type="color" value={cfg.text_color || "#5C4A38"} onChange={(e) => upd({ text_color: e.target.value })}
                    disabled={dis} className="rounded-none h-9 w-24 p-1" data-testid="sb-wc-text-color" />
          </Field>
        </div>
        <Field label="Logo">
          <ImageUpload ws={ws} purpose="logo" value={cfg.logo_url}
                        onChange={(url) => upd({ logo_url: url })}
                        testid="sb-wc-logo" disabled={dis} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Heading font">
            <select value={cfg.heading_font || "cormorant"}
                    onChange={(e) => upd({ heading_font: e.target.value })}
                    disabled={dis}
                    className="w-full rounded-none h-9 px-2 border border-input bg-transparent text-sm"
                    data-testid="sb-wc-heading-font">
              {WIDGET_FONT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Body font">
            <select value={cfg.body_font || "system"}
                    onChange={(e) => upd({ body_font: e.target.value })}
                    disabled={dis}
                    className="w-full rounded-none h-9 px-2 border border-input bg-transparent text-sm"
                    data-testid="sb-wc-body-font">
              {WIDGET_FONT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Copy">
        <Field label="CTA button label">
          <Input value={cfg.cta_label || ""} onChange={(e) => upd({ cta_label: e.target.value })}
                  disabled={dis} className="rounded-none h-9" data-testid="sb-wc-cta" placeholder="Book now" />
        </Field>
        <Field label="Terms & conditions (HTML allowed, shown near CTA)">
          <Textarea value={cfg.terms_html || ""} onChange={(e) => upd({ terms_html: e.target.value })}
                    disabled={dis} rows={3} className="rounded-none text-xs font-mono" data-testid="sb-wc-terms" />
        </Field>
        <Field label="Thank-you message (shown after payment)">
          <Textarea value={cfg.thank_you_html || ""} onChange={(e) => upd({ thank_you_html: e.target.value })}
                    disabled={dis} rows={3} className="rounded-none text-xs font-mono" data-testid="sb-wc-thankyou" />
        </Field>
        <Field label='Additional services disclaimer / header (replaces the default "Studio, equipment & crew are booked instantly…" note)'>
          <div className="grid md:grid-cols-2 gap-3 items-start">
            <div>
              <Textarea value={cfg.additional_services_html || ""} onChange={(e) => upd({ additional_services_html: e.target.value })}
                        disabled={dis} rows={6} className="rounded-none text-xs font-mono"
                        placeholder="<p>Need something more? Pick any extras and we'll send a separate quote by email.</p>"
                        data-testid="sb-wc-services-html" />
              <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                Rendered as rich HTML directly above the "Additional services" checkbox grid on the booking widget. Leave blank to use the default message.
              </p>
            </div>
            {/* Feb 2026 · Live preview tile — renders exactly the way
                 the widget shows the disclaimer so admins can see
                 formatting, line breaks, and bold tags without having
                 to open the public widget in a second tab. */}
            <div data-testid="sb-wc-services-html-preview">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
                Live preview
              </div>
              {cfg.additional_services_html ? (
                <div className="lx-services-html"
                     style={{ fontSize: 13, color: "#241A11", padding: "10px 12px",
                                background: "#FAF2E4",
                                border: "1px solid #ECD9B2",
                                borderLeft: "3px solid #B98B33",
                                lineHeight: 1.55 }}
                     dangerouslySetInnerHTML={{ __html: cfg.additional_services_html }} />
              ) : (
                <div style={{ fontSize: 12, color: "#5C4A38",
                                padding: "8px 12px",
                                background: "#FAF2E4",
                                border: "1px solid #ECD9B2",
                                borderLeft: "3px solid #B98B33" }}>
                  ℹ Studio, equipment &amp; crew above are booked instantly. Anything you pick below is a special request — <b>we'll send a separate quotation over email</b> after the booking is confirmed.
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1 italic leading-snug">
                This is the default message shown when the field above is blank.
              </p>
            </div>
          </div>
        </Field>
      </Section>

      <Section title="Landing chooser (customizable tiles)">
        <div className="text-[11px] text-muted-foreground -mt-1 mb-1">
          When ON, the booking page opens with your custom tile list — the Studio tile continues to the booking flow, every other tile deep-links to its enquiry form. Add as many tiles as you like.
        </div>
        <ToggleRow label="Show landing chooser"
                   value={!!cfg.show_entry_chooser}
                   onChange={(v) => upd({ show_entry_chooser: v })}
                   disabled={dis} testid="sb-wc-entry-chooser" />
        {cfg.show_entry_chooser && (
          <EntryTilesEditor
            tiles={cfg.entry_tiles || DEFAULT_ENTRY_TILES(cfg)}
            onChange={(tiles) => upd({ entry_tiles: tiles })}
            disabled={dis}
            ws={ws}
          />
        )}
      </Section>

      <Section title="Required customer fields">
        <ToggleRow label="Require phone number"
                   value={cfg.require_phone !== false}
                   onChange={(v) => upd({ require_phone: v })}
                   disabled={dis} testid="sb-wc-req-phone" />
        <ToggleRow label="Require company name"
                   value={!!cfg.require_company}
                   onChange={(v) => upd({ require_company: v })}
                   disabled={dis} testid="sb-wc-req-company" />
        <ToggleRow label="Require GSTIN (for invoicing)"
                   value={!!cfg.require_gstin}
                   onChange={(v) => upd({ require_gstin: v })}
                   disabled={dis} testid="sb-wc-req-gstin" />
      </Section>

      <Section title="Sections shown on booking page">
        <ToggleRow label="Show packages" value={cfg.show_packages !== false}
                   onChange={(v) => upd({ show_packages: v })} disabled={dis} testid="sb-wc-show-packages" />
        <ToggleRow label="Show equipment add-ons" value={cfg.show_equipment !== false}
                   onChange={(v) => upd({ show_equipment: v })} disabled={dis} testid="sb-wc-show-equipment" />
        <ToggleRow label="Show crew add-ons" value={cfg.show_crew !== false}
                   onChange={(v) => upd({ show_crew: v })} disabled={dis} testid="sb-wc-show-crew" />
        <ToggleRow label="Allow offsite shoots" value={cfg.show_offsite !== false}
                   onChange={(v) => upd({ show_offsite: v })} disabled={dis} testid="sb-wc-show-offsite" />
      </Section>

      <Section title="Email sender identity">
        <div className="text-[11px] text-muted-foreground -mt-1 mb-1">
          Applied to every studio-module email — booking confirmations, reminders, deposit refunds, delivery sign-off invites.
          Pick a mail profile below to route via a brand-specific outbound relay (SMTP / Resend / ZeptoMail).
        </div>
        <StudioMailProfilePicker ws={ws} value={cfg.mail_profile_id}
                                   onChange={(v) => upd({ mail_profile_id: v })}
                                   disabled={dis} />
        <Field label="From name (overrides the profile's From-Name)">
          <Input value={cfg.email_from_name || ""} onChange={(e) => upd({ email_from_name: e.target.value })}
                  disabled={dis} className="rounded-none h-9" data-testid="sb-wc-from-name"
                  placeholder="Story & Fame Studio" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Reply-to name">
            <Input value={cfg.email_reply_to_name || ""} onChange={(e) => upd({ email_reply_to_name: e.target.value })}
                    disabled={dis} className="rounded-none h-9" data-testid="sb-wc-reply-name"
                    placeholder="Studio Ops" />
          </Field>
          <Field label="Reply-to email">
            <Input value={cfg.email_reply_to || ""} onChange={(e) => upd({ email_reply_to: e.target.value })}
                    disabled={dis} className="rounded-none h-9" data-testid="sb-wc-reply-email"
                    placeholder="studio@yourdomain.com" />
          </Field>
        </div>
      </Section>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="outline" className="rounded-none" onClick={load}>Reset</Button>
        <Button className="rounded-none" onClick={save} disabled={busy || dis} data-testid="sb-wc-save">
          Save changes
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function ToggleRow({ label, value, onChange, disabled, testid }) {
  return (
    <label className="flex items-center justify-between border border-border px-3 py-2 cursor-pointer">
      <span className="text-xs">{label}</span>
      <input type="checkbox" checked={!!value} disabled={disabled}
              onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    </label>
  );
}

// Feb 2026 · R268mmm · Custom booking-URL slug editor. Validates
// kebab-case client-side, shows the live URL that customers will
// use, and provides a one-tap Copy button. Server enforces global
// uniqueness on save — collisions surface as toast errors.
function BookingSlugField({ cfg, upd, dis }) {
  const raw = cfg.booking_slug || "";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://example.com";
  const preview = raw
    ? `${origin}/book/${raw}`
    : `${origin}/book/<workspace-id>`;
  const looksValid = raw === "" || /^[a-z0-9](?:[a-z0-9-]{1,58}[a-z0-9])?$/.test(raw);
  const copy = () => {
    if (!raw) return;
    navigator.clipboard?.writeText(preview);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-muted-foreground -mt-1">
        Set a memorable slug — e.g. <code className="font-mono">acme-studios</code> — instead of the long workspace ID.
        The old <code className="font-mono">/book/{"{workspace-id}"}</code> URL keeps working.
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">{origin.replace(/^https?:\/\//, "")}/book/</span>
        <Input value={raw}
                onChange={(e) => upd({ booking_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                disabled={dis}
                className={`rounded-none h-9 font-mono ${!looksValid ? "border-red-500" : ""}`}
                placeholder="acme-studios"
                data-testid="sb-wc-booking-slug" />
        <Button type="button" variant="outline" className="rounded-none h-9"
                 onClick={copy} disabled={!raw || !looksValid}
                 data-testid="sb-wc-booking-slug-copy">Copy URL</Button>
      </div>
      {!looksValid && (
        <div className="text-[11px] text-red-600">
          Slug must be 3–60 chars — lowercase letters, digits and hyphens only, no leading or trailing hyphen.
        </div>
      )}
      {raw && looksValid && (
        <div className="text-[11px] text-muted-foreground">
          Preview: <a href={preview} target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline">{preview}</a>
        </div>
      )}
    </div>
  );
}


// R268xxx · Feb 2026 · Quick share URL — the canonical /book/<slug>
// link with a one-tap Copy button. Sits right under the layout
// selector in the widget config so admins can grab the link without
// hunting through the deep-link builder for the common case.
function QuickShareUrl({ cfg, ws }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slug = cfg.booking_slug || ws;
  const link = `${origin}/book/${slug}`;
  const copy = () => {
    navigator.clipboard?.writeText(link);
    toast.success("Booking link copied");
  };
  return (
    <div className="flex items-center gap-2 mt-2 mb-3 bg-muted/20 border border-border p-2.5"
          data-testid="sb-wc-quick-share">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Share this booking page
        </div>
        <a href={link} target="_blank" rel="noopener noreferrer"
            className="text-xs font-mono text-primary hover:underline truncate block"
            data-testid="sb-wc-quick-share-link" title={link}>
          {link}
        </a>
      </div>
      <Button type="button" variant="outline" onClick={copy}
               className="rounded-none h-8 text-xs"
               data-testid="sb-wc-quick-share-copy">
        Copy link
      </Button>
    </div>
  );
}


// Feb 2026 · R268sss · Social preview editor + copy-link helper.
// The WhatsApp/iMessage crawler doesn't run JS, so we can't just tag
// the SPA — we ship a backend OG-preview page at /api/v1/og/b/<slug>
// that returns HTML with the right meta tags and 200ms-redirects
// real browsers to the actual widget. Admins share THIS URL for a
// rich preview.
function SocialPreviewField({ cfg, upd, dis, ws }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const backend = process.env.REACT_APP_BACKEND_URL || origin;
  const slug = cfg.booking_slug || ws;
  const previewUrl = `${backend}/api/v1/og/b/${slug}`;
  const copy = () => {
    navigator.clipboard?.writeText(previewUrl);
    toast.success("Preview link copied — paste into WhatsApp / iMessage to test");
  };
  return (
    <div className="space-y-3 mt-3">
      <Field label="OG preview image (WhatsApp / iMessage / LinkedIn preview)">
        <ImageUpload ws={ws} purpose="hero"
                      value={cfg.og_image_url || ""}
                      onChange={(url) => upd({ og_image_url: url })}
                      testid="sb-wc-og-image" disabled={dis}
                      cropMode="1200x630" />
        <div className="text-[11px] text-muted-foreground mt-1">
          Recommended 1200×630. If empty, falls back to the first studio's cover / logo.
        </div>
      </Field>
      <Field label="OG description (2 short lines that appear under the title)">
        <Input value={cfg.og_description || ""}
                onChange={(e) => upd({ og_description: e.target.value })}
                disabled={dis} maxLength={160}
                placeholder="Reserve a professional shoot slot in Andheri — same-day confirmation."
                className="rounded-none h-9"
                data-testid="sb-wc-og-description" />
      </Field>
      <div className="flex items-center gap-2 bg-muted/20 p-3 border border-border">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Preview URL for social apps</div>
          <div className="text-xs font-mono truncate text-primary" title={previewUrl}>{previewUrl}</div>
        </div>
        <Button type="button" variant="outline" className="rounded-none h-9"
                 onClick={copy} data-testid="sb-wc-og-copy">Copy preview link</Button>
      </div>
    </div>
  );
}


// Feb 2026 · R268sss · Interactive deep-link builder. Admins pick
// which studio / space / date / time / layout should be pre-selected,
// and the widget generates a shareable URL that lands customers past
// step 1. Ideal for paid-ad campaigns.
function DeepLinkBuilder({ cfg, ws }) {
  const [studios, setStudios] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [studioId, setStudioId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [mode, setMode] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const [rs, ru] = await Promise.all([
          api.get(`/workspaces/${ws}/studio/studios`),
          api.get(`/workspaces/${ws}/studio/spaces`),
        ]);
        setStudios(rs.data.studios || []);
        setSpaces(ru.data.spaces || []);
      } catch { /* silent */ }
    })();
  }, [ws]);
  const filteredSpaces = studioId
    ? spaces.filter((s) => s.studio_id === studioId)
    : spaces;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slug = cfg.booking_slug || ws;
  const q = new URLSearchParams();
  if (studioId) q.set("studio", studioId);
  if (spaceId) q.set("space", spaceId);
  if (date) q.set("date", date);
  if (start) q.set("start", start);
  if (mode) q.set("mode", mode);
  const link = `${origin}/book/${slug}${q.toString() ? `?${q}` : ""}`;
  const copy = () => {
    navigator.clipboard?.writeText(link);
    toast.success("Deep link copied");
  };
  return (
    <div className="space-y-2 mt-3 p-3 bg-muted/20 border border-border" data-testid="sb-wc-deep-link">
      <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Deep-link builder — for ads & campaigns</div>
      <div className="grid grid-cols-2 gap-2">
        <select value={studioId} onChange={(e) => { setStudioId(e.target.value); setSpaceId(""); }}
                 className="h-9 border border-input bg-background px-2 text-xs" data-testid="sb-wc-deep-studio">
          <option value="">— Any studio —</option>
          {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={spaceId} onChange={(e) => setSpaceId(e.target.value)}
                 className="h-9 border border-input bg-background px-2 text-xs" data-testid="sb-wc-deep-space">
          <option value="">— Any space —</option>
          {filteredSpaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="rounded-none h-9" data-testid="sb-wc-deep-date" />
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                className="rounded-none h-9" data-testid="sb-wc-deep-start" />
        <select value={mode} onChange={(e) => setMode(e.target.value)}
                 className="h-9 border border-input bg-background px-2 text-xs col-span-2" data-testid="sb-wc-deep-mode">
          <option value="">Use widget default layout</option>
          <option value="steps">Force stepped layout</option>
          <option value="compact">Force compact layout</option>
        </select>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <div className="flex-1 text-xs font-mono truncate text-primary" title={link}>{link}</div>
        <Button type="button" variant="outline" className="rounded-none h-8"
                 onClick={copy} data-testid="sb-wc-deep-copy">Copy link</Button>
      </div>
    </div>
  );
}


// Feb 2026 · R268jjj · Curated font choices for the widget config picker.
const WIDGET_FONT_OPTIONS = [
  { value: "system",       label: "System (San-Francisco / Roboto)" },
  { value: "inter",        label: "Inter (modern sans)" },
  { value: "playfair",     label: "Playfair Display (editorial serif)" },
  { value: "cormorant",    label: "Cormorant Garamond (luxe serif)" },
  { value: "dmserif",      label: "DM Serif Display (bold serif)" },
  { value: "spacegrotesk", label: "Space Grotesk (tech sans)" },
  { value: "lora",         label: "Lora (magazine serif)" },
  { value: "manrope",      label: "Manrope (rounded sans)" },
  { value: "fraunces",     label: "Fraunces (expressive serif)" },
  { value: "ibmplex",      label: "IBM Plex Sans (utility)" },
  { value: "merriweather", label: "Merriweather (readable serif)" },
  { value: "poppins",      label: "Poppins (friendly sans)" },
];

// Feb 2026 · R268jjj · Default tiles carried forward from the legacy
// three-flow layout so admins that upgrade see a sane starting point.
const DEFAULT_ENTRY_TILES = (cfg) => [
  { kind: "studio", label: cfg.entry_studio_label || "Book a studio",
    description: "Pick a space, date, extras — pay online." },
  { kind: "enquiry", slug: "offsite-shoot",
    label: cfg.entry_offsite_label || "Offsite shoot enquiry",
    description: "Tell us the brief — we'll send a tailored quote." },
  { kind: "enquiry", slug: cfg.entry_other_type_slug || "custom-shoot",
    label: cfg.entry_other_label || "Digital · design · production",
    description: "Any other request — briefs, quotes, custom." },
];

// Feb 2026 · R268jjj · Dynamic tile editor. Studio tile is FIXED at the
// top (always present, label + description editable, cannot be removed
// or reordered) and enquiry tiles live in a re-orderable list below
// it. This mirrors what admins see on the public booking page — Studio
// is the anchor entry point, everything else is a bespoke enquiry
// deep-link.
function EntryTilesEditor({ tiles, onChange, disabled, ws }) {
  const [types, setTypes] = useState([]);
  useEffect(() => {
    api.get(`/workspaces/${ws}/enquiries/types`)
      .then((r) => setTypes(r.data?.types || r.data || []))
      .catch(() => setTypes([]));
  }, [ws]);

  // R268yyy · Normalise so Studio is always position 0. If an admin
  // previously removed it (legacy config), we synthesise a fresh one
  // rather than losing the entry point.
  const studioIdx = tiles.findIndex((t) => (t.kind || "enquiry") === "studio");
  const studio = studioIdx >= 0
    ? tiles[studioIdx]
    : { kind: "studio", label: "Book a studio", description: "Pick a space, date, extras — pay online." };
  const enquiries = tiles
    .map((t, i) => ({ t, i }))
    .filter(({ t, i }) => i !== studioIdx && (t.kind || "enquiry") !== "studio")
    .map(({ t }) => ({ ...t, kind: "enquiry" }));

  const commit = (nextStudio, nextEnquiries) => {
    onChange([nextStudio, ...nextEnquiries]);
  };

  const patchStudio = (next) => commit({ ...studio, ...next }, enquiries);
  const patchEnquiry = (i, next) => {
    const copy = [...enquiries];
    copy[i] = { ...copy[i], ...next };
    commit(studio, copy);
  };
  const removeEnquiry = (i) => {
    const copy = enquiries.filter((_, j) => j !== i);
    commit(studio, copy);
  };
  const moveEnquiry = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= enquiries.length) return;
    const copy = [...enquiries];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    commit(studio, copy);
  };
  const addEnquiry = () => commit(studio, [
    ...enquiries,
    { kind: "enquiry", label: "New enquiry", description: "", slug: types[0]?.slug || "" },
  ]);

  return (
    <div className="space-y-3" data-testid="sb-wc-entry-tiles">
      {/* Fixed Studio tile — always first, cannot be removed or reordered. */}
      <div className="border border-primary/40 bg-primary/5 p-3 space-y-2"
             data-testid="sb-wc-entry-tile-studio">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            Studio tile · fixed
          </span>
          <span className="text-[10px] text-muted-foreground italic">Always shown first · continues to the booking flow</span>
        </div>
        <Input value={studio.label || ""}
                 onChange={(e) => patchStudio({ label: e.target.value })}
                 disabled={disabled} className="rounded-none h-8"
                 placeholder="Studio tile label (e.g. Book a studio)"
                 data-testid="sb-wc-entry-tile-studio-label" />
        <Input value={studio.description || ""}
                 onChange={(e) => patchStudio({ description: e.target.value })}
                 disabled={disabled} className="rounded-none h-8"
                 placeholder="Short subtitle under the label"
                 data-testid="sb-wc-entry-tile-studio-desc" />
      </div>

      {/* Enquiry tiles — admin adds / reorders / removes. */}
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">
        Enquiry tiles ({enquiries.length})
      </div>
      {enquiries.map((t, i) => {
        const currentSlug = t.slug || "";
        const slugKnown = types.some((ty) => ty.slug === currentSlug);
        return (
          <div key={i} className="border border-border p-3 space-y-2"
                 data-testid={`sb-wc-entry-tile-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground w-14">
                Tile {i + 2}
              </span>
              <span className="text-[10px] text-muted-foreground italic">Deep-links to an enquiry form</span>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0"
                       disabled={disabled || i === 0} onClick={() => moveEnquiry(i, -1)}
                       data-testid={`sb-wc-entry-tile-up-${i}`}>↑</Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0"
                       disabled={disabled || i === enquiries.length - 1} onClick={() => moveEnquiry(i, 1)}
                       data-testid={`sb-wc-entry-tile-down-${i}`}>↓</Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-rose-600 hover:text-rose-700"
                       disabled={disabled} onClick={() => removeEnquiry(i)}
                       data-testid={`sb-wc-entry-tile-remove-${i}`}>Remove</Button>
            </div>
            <Input value={t.label || ""} onChange={(e) => patchEnquiry(i, { label: e.target.value })}
                    disabled={disabled} className="rounded-none h-8" placeholder="Tile label (e.g. Offsite shoot)"
                    data-testid={`sb-wc-entry-tile-label-${i}`} />
            <Input value={t.description || ""} onChange={(e) => patchEnquiry(i, { description: e.target.value })}
                    disabled={disabled} className="rounded-none h-8" placeholder="Short subtitle under the label"
                    data-testid={`sb-wc-entry-tile-desc-${i}`} />
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Opens enquiry form</Label>
              <select value={currentSlug}
                        onChange={(e) => patchEnquiry(i, { slug: e.target.value })}
                        disabled={disabled}
                        className="w-full rounded-none h-8 px-2 border border-input bg-transparent text-xs"
                        data-testid={`sb-wc-entry-tile-slug-${i}`}>
                <option value="">— Pick enquiry type —</option>
                {types.map((ty) => (
                  <option key={ty.slug || ty.id} value={ty.slug}>
                    {ty.name}{ty.slug ? ` · /${ty.slug}` : ""}
                  </option>
                ))}
                {/* Preserve legacy slugs (e.g. "offsite-shoot", "custom-shoot")
                     that aren't in the enquiry_types collection yet — otherwise
                     switching to the dropdown would silently blank out the tile. */}
                {currentSlug && !slugKnown && (
                  <option value={currentSlug}>{currentSlug} (legacy)</option>
                )}
              </select>
              {types.length === 0 && (
                <div className="text-[10px] text-amber-600 mt-1">
                  No enquiry types found. Create some in Sales → Enquiries → Types first, then come back.
                </div>
              )}
            </div>
          </div>
        );
      })}
      {enquiries.length === 0 && (
        <div className="border border-dashed border-border p-4 text-center text-[11px] text-muted-foreground italic"
              data-testid="sb-wc-entry-tiles-empty">
          No enquiry tiles yet — customers will only see the Studio booking tile.
          Add one below to deep-link them to a bespoke enquiry form.
        </div>
      )}
      <Button size="sm" variant="outline" className="rounded-none w-full h-9" disabled={disabled}
               onClick={addEnquiry} data-testid="sb-wc-entry-tile-add">
        + Add enquiry tile
      </Button>
    </div>
  );
}

function StudioMailProfilePicker({ ws, value, onChange, disabled }) {
  const [profiles, setProfiles] = useState([]);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/mail-profiles`)
       .then((r) => setProfiles((r.data.profiles || []).filter((p) => p.active !== false)))
       .catch(() => {});
  }, [ws]);
  return (
    <div>
      <Label className="text-xs">Mail profile</Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || "")}
              disabled={disabled} data-testid="sb-wc-mail-profile"
              className="w-full h-9 border border-border bg-background text-xs rounded-none px-2 mt-1">
        <option value="">Workspace default</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · {p.provider.toUpperCase()} · {p.sender_email}
          </option>
        ))}
      </select>
      <div className="text-[10px] text-muted-foreground mt-1">
        {profiles.length === 0 ? (
          <>No profiles yet — <a href="/app/settings/mail-profiles" target="_blank" rel="noopener noreferrer" className="text-primary underline">create one</a> to route via a branded outbound relay.</>
        ) : (
          <>Applied to every studio-module email.</>
        )}
      </div>
    </div>
  );
}
